import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../utils/api";
import { Catalogue, CatalogueGame } from "./types";
import { API, errMsg, fmtAmt, Header, ErrorBox, pageCls, chipCls } from "./shared";

// The live provider catalogue (public /originalgames/games). Limits arrive in
// minor units; our currency exponents turn them into amounts.
export default function Games() {
  const [games, setGames] = useState<CatalogueGame[]>([]);
  const [meta, setMeta] = useState<Catalogue | null>(null);
  const [exps, setExps] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cat, cur] = await Promise.all([
        apiFetch<Catalogue>("/originalgames/games"),
        apiFetch<{ exponents: Record<string, number> }>(API + "/currencies"),
      ]);
      const body = cat.data;
      setGames(Array.isArray(body) ? body : body.data || []);
      setMeta(cat);
      setExps(cur.exponents || {});
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const major = (v: string, c: string) => (exps[c] === undefined ? `${v} (minor)` : fmtAmt(Number(v) / Math.pow(10, exps[c])));

  return (
    <div className={pageCls}>
      <Header title="Original Games — Games" subtitle="Live catalogue from the provider, with RTP and bet limits" onRefresh={load} loading={loading} />
      <ErrorBox error={error} />
      {meta && (
        <div className="flex flex-wrap gap-2 text-xs">
          <span className={chipCls}>Environment: <b>{meta.environment}</b></span>
          <span className={chipCls}>Mode: <b>{meta.mode}</b></span>
          <span className={chipCls}>Wallet can settle: {(meta.settleable_currencies || []).join(", ")}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {games.map((g) => {
          const live = g.enabled && g.status === "live";
          return (
            <div key={g.game_code} className="bg-[#0E1831] rounded-xl border border-[#1E2D55] p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-lg">{g.name}</div>
                  <div className="text-xs text-[#878AA2] font-mono">{g.game_code} · {g.category}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${live ? "bg-[#0ECC68]/15 text-[#0ECC68]" : "bg-[#E01B4F]/15 text-[#E01B4F]"}`}>
                    {g.status}{g.enabled ? "" : " · disabled"}
                  </span>
                  <span className="text-xs text-[#878AA2]">RTP {(Number(g.rtp) * 100).toFixed(2)}%</span>
                </div>
              </div>
              <table className="w-full text-xs">
                <thead><tr className="text-[#878AA2] uppercase">
                  <th className="text-left py-1">Cur.</th><th className="text-right py-1">Min bet</th><th className="text-right py-1">Max bet</th><th className="text-right py-1">Max win</th>
                </tr></thead>
                <tbody>
                  {Object.keys(g.limits || {}).map((c) => (
                    <tr key={c} className="border-t border-[#1E2D55]">
                      <td className="py-1 font-semibold">{c}</td>
                      <td className="py-1 text-right font-mono">{major(g.limits[c].min_bet, c)}</td>
                      <td className="py-1 text-right font-mono">{major(g.limits[c].max_bet, c)}</td>
                      <td className="py-1 text-right font-mono">{major(g.limits[c].max_win, c)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
        {!loading && !error && games.length === 0 && <p className="text-[#878AA2]">The provider returned no games.</p>}
      </div>
    </div>
  );
}
